import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toggleLikeEvent, fetchLikedEvents } from '../../store/slices/eventsSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark as faSolidBookmark } from '@fortawesome/free-solid-svg-icons';
import { faBookmark as faRegularBookmark } from '@fortawesome/free-regular-svg-icons';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import { parseDJsFromDescription, getCleanDJName } from '../../utils/djParser';
import './EventDetailModal.css';

const EventDetailModal = ({ event, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { likedEvents } = useSelector((state) => state.events);
  const modalRef = useRef(null);
  const modalBodyRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const [translateY, setTranslateY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  
  const isLiked = event ? likedEvents.includes(event.id) : false;

  // 여러 장르를 처리하기 위한 유틸리티 함수들
  const parseGenres = (genreString) => {
    if (!genreString) return [];
    return genreString.split(',').map(genre => genre.trim().toLowerCase()).filter(genre => genre);
  };

  const getPrimaryGenre = (genreString) => {
    const genres = parseGenres(genreString);
    return genres.length > 0 ? genres[0] : '';
  };

  const getValidGenres = (genreString) => {
    const validGenres = ['edm', 'hiphop', 'techno', 'house'];
    const genres = parseGenres(genreString);
    return genres.filter(genre => validGenres.includes(genre));
  };

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // Close modal on escape key
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      return () => {
        document.body.style.overflow = 'auto';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  // 스와이프 이벤트 핸들러
  const handleTouchStart = (e) => {
    // 모달 컨텐츠의 스크롤 위치 확인
    const modalContent = modalRef.current;
    if (!modalContent) return;
    
    const isAtTop = modalContent.scrollTop === 0;
    
    // 터치가 상단 영역에서 시작되었는지 확인 (banner 영역)
    const touchY = e.touches[0].clientY;
    const modalRect = modalContent.getBoundingClientRect();
    const relativeY = touchY - modalRect.top;
    const isInTopArea = relativeY < 300; // 상단 300px 영역에서만 허용
    
    // 맨 위에 있고 상단 영역에서 터치할 때만 허용
    if (isAtTop && isInTopArea) {
      touchStartY.current = e.touches[0].clientY;
      setTranslateY(0);
    } else {
      touchStartY.current = null; // 터치 이벤트 비활성화
    }
  };

  const handleTouchMove = (e) => {
    // 터치 시작이 유효하지 않으면 무시
    if (touchStartY.current === null) return;
    
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - touchStartY.current;
    
    // 아래쪽으로만 드래그 허용
    if (deltaY > 0) {
      // 기본 스크롤 동작 방지 (모달 닫기 제스처일 때만)
      e.preventDefault();
      setTranslateY(deltaY);
    }
  };

  const handleTouchEnd = (e) => {
    // 터치 시작이 유효하지 않으면 무시
    if (touchStartY.current === null) return;
    
    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchEndY - touchStartY.current;
    
    // 최소 스와이프 거리
    const minSwipeDistance = 100;
    
    if (deltaY > minSwipeDistance) {
      setIsClosing(true);
      
      // 현재 위치에서부터 자연스럽게 아래로 슬라이드
      const currentY = translateY;
      const targetY = window.innerHeight;
      const duration = 300;
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // easeOut 곡선 적용
        const easeOutProgress = 1 - Math.pow(1 - progress, 3);
        const newY = currentY + (targetY - currentY) * easeOutProgress;
        
        setTranslateY(newY);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          onClose();
          setIsClosing(false);
          setTranslateY(0);
        }
      };
      
      requestAnimationFrame(animate);
    } else {
      // 스와이프가 충분하지 않으면 원래 위치로 복귀
      setTranslateY(0);
    }
    
    // 터치 상태 리셋
    touchStartY.current = null;
  };

  useEffect(() => {
    // 사용자가 로그인되어 있고 모달이 열렸을 때 좋아요 목록 가져오기
    if (isAuthenticated && user && isOpen) {
      dispatch(fetchLikedEvents(user.id));
    }
  }, [dispatch, isAuthenticated, user, isOpen]);

  // DJ 정보 파싱 (early return 이전에 위치)
  const djList = useMemo(() => {
    return parseDJsFromDescription(event?.description);
  }, [event?.description]);

  if (!isOpen || !event) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleLikeClick = (e) => {
    e.stopPropagation();
    if (!user || !isAuthenticated) {
      // 로그인하지 않은 경우 처리 (옵션)
      alert('로그인이 필요합니다.');
      return;
    }
    
    dispatch(toggleLikeEvent({ 
      userId: user.id, 
      eventId: event.id, 
      isLiked 
    }));
  };

  const handleInstagramClick = (e) => {
    e.stopPropagation();
    if (event.instagram_url) {
      window.open(event.instagram_url, '_blank');
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    // "HH:MM:SS" 형태를 "HH:MM"로 변환
    return timeStr.slice(0, 5);
  };



  // DJ 링크가 포함된 설명 렌더링
  const renderEnhancedDescription = (description) => {
    if (!description) return '설명이 없습니다.';
    
    if (djList.length === 0) {
      return description;
    }

    // DJ 정보를 하이라이트해서 표시
    let processedText = description;
    const elements = [];
    
    // 모든 DJ 패턴과 위치를 수집
    const djMatches = [];
    djList.forEach((dj, index) => {
      const djPattern = dj.handle 
        ? `${dj.name} @${dj.handle}`
        : dj.name;
      
      const djIndex = processedText.indexOf(djPattern);
      if (djIndex !== -1) {
        djMatches.push({
          index: djIndex,
          length: djPattern.length,
          dj: dj,
          djIndex: index,
          pattern: djPattern
        });
      }
    });
    
    // 위치 순서대로 정렬
    djMatches.sort((a, b) => a.index - b.index);
    
    let currentIndex = 0;
    
    djMatches.forEach((match) => {
      // DJ 패턴 이전 텍스트 추가
      if (match.index > currentIndex) {
        elements.push(processedText.substring(currentIndex, match.index));
      }
      
      // 깔끔한 DJ 이름 가져오기
      const cleanName = getCleanDJName(match.dj.name, match.dj.handle);
      
      // DJ 링크 추가 (이름만 + 인스타그램 아이콘)
      elements.push(
        <span
          key={`dj-${match.djIndex}`}
          className="dj-link"
          onClick={() => match.dj.instagram && window.open(match.dj.instagram, '_blank')}
          title={match.dj.instagram ? `${cleanName} 인스타그램으로 이동` : cleanName}
        >
          <span className="dj-name">{cleanName}</span>
          {match.dj.instagram && (
            <FontAwesomeIcon 
              icon={faInstagram} 
              className="dj-instagram-icon"
            />
          )}
        </span>
      );
      
      currentIndex = match.index + match.length;
    });
    
    // 남은 텍스트 추가
    if (currentIndex < processedText.length) {
      elements.push(processedText.substring(currentIndex));
    }
    
    return elements.length > 0 ? elements : description;
  };

  return ReactDOM.createPortal(
    <div className="event-detail-modal-overlay" onClick={handleOverlayClick}>
      <div 
        className={`event-detail-modal-content ${isClosing ? 'closing' : ''}`}
        data-direction={isClosing ? 'down' : ''}
        ref={modalRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateY(${translateY}px)`,
          transition: (translateY === 0 && !isClosing) ? 'transform 0.3s ease-out' : 'none'
        }}
      >
        <button className="event-detail-close-button" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        
        <div className="event-detail-modal-body" ref={modalBodyRef}>
          <div className={`event-banner ${getPrimaryGenre(event.genre)}`}>
            {event.poster_image_url && (
              <img 
                src={event.poster_image_url} 
                alt={event.title} 
                className="event-banner-background"
              />
            )}
            
            {/* 북마크 버튼 */}
            {isAuthenticated && (
              <button className="event-bookmark-button" onClick={handleLikeClick}>
                <FontAwesomeIcon
                  icon={isLiked ? faSolidBookmark : faRegularBookmark}
                  className="bookmark-icon"
                />
              </button>
            )}
            
            {/* 인스타그램 링크 */}
            {event.instagram_url && (
              <button className="event-instagram-button" onClick={handleInstagramClick}>
                <FontAwesomeIcon icon={faInstagram} />
              </button>
            )}
            
            <h3>{event.title}</h3>
          </div>
          
          <div className="event-info">
            <div className="info-row">
              <span className="info-label">장르</span>
              <div className="genre-tags-container">
                {getValidGenres(event.genre).map((genre, index) => (
                  <span key={`modal-genre-${index}`} className={`info-value genre-tag ${genre}`}>
                    {genre.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
            <div className="info-row">
              <span className="info-label">날짜</span>
              <span className="info-value">{event.date}</span>
            </div>
            <div className="info-row">
              <span className="info-label">시간</span>
              <span className="info-value">
                {(() => {
                  if (event.start_time && event.end_time) {
                    return `${formatTime(event.start_time)} - ${formatTime(event.end_time)}`;
                  } else if (event.start_time) {
                    return `${formatTime(event.start_time)} ~ LATE`;
                  } else if (event.end_time) {
                    return `~ ${formatTime(event.end_time)}`;
                  } else if (event.time && event.time !== 'N/A') {
                    return event.time;
                  } else {
                    return '시간 미정';
                  }
                })()}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">북마크</span>
              <span className="info-value">{event.like_count || 0}개</span>
            </div>
            <div className="info-row">
              <span className="info-label">장소</span>
              <span className="info-value">
                {event.club && event.club.name ? event.club.name : (event.location || '장소 정보 없음')}
              </span>
            </div>
            {event.club && event.club.address && (
              <div className="info-row">
                <span className="info-label">주소</span>
                <span className="info-value">{event.club.address}</span>
              </div>
            )}
            <div className="info-row description">
              <span className="info-label">상세설명</span>
              <p className="info-value description-text">
                {renderEnhancedDescription(event.description)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EventDetailModal; 